import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import {
  Validators,
  FormBuilder,
  FormGroup,
  FormControl,
} from '@angular/forms';
import { environment } from 'src/environments/environment'; 
import {
  DocumentCategory,
  DocumentCategoryType,
  DocumentGroup,
} from 'src/app/core/enums/document.enum';
import {
  BusinessDocumentUpdateModel,
  BusinessServiceDocumentModel,
  CustomDocumentModel,
} from 'src/app/modules/shared/models/document.model';
import * as AWS from 'aws-sdk';
import { AlertService } from '../../../services/alert/alert.service';
import { TradieService } from 'src/app/modules/onboarding/shared/services/tradie.service';


@Component({
  selector: 'app-shared-service-document-upload-popup',
  templateUrl: './shared-service-document-upload-popup.component.html',
  styleUrls: ['./shared-service-document-upload-popup.component.scss'],
  providers: [DatePipe]
})
export class SharedServiceDocumentUploadPopupComponent implements OnInit {

  formServiceDocument!: FormGroup;
  submitted = false;
  isLoading = false;

  formData!: FormData;
  reader!: FileReader;
  fileData!: File;
  fileName!: string;
  currentURL!: string;
  imgURL: any;
  public imagePath!: string;
  minExpireData = new Date();

  serviceDocumentData = new BusinessServiceDocumentModel();

  documentType = DocumentCategoryType;
  documentCategory = DocumentCategory;
  
  documentGroup = DocumentGroup;
  documentGroupList : any[] = [
    { value: this.documentGroup.Mandatory, viewValue: 'Mandatory'},
    { value: this.documentGroup.Additional, viewValue: 'Additional'}
  ]

  constructor(
    private tradieService: TradieService,
    private alertService: AlertService,
    private formBuilder: FormBuilder,
    private datePipe: DatePipe,
    @Inject(MAT_DIALOG_DATA) public documentData: CustomDocumentModel,
    public dialogRef: MatDialogRef<SharedServiceDocumentUploadPopupComponent>
  ) {}

  ngOnInit(): void {
    this.formServiceDocument = this.formBuilder.group({
      name: [ '', Validators.required],
      link: ['', Validators.required],
      documentType: ['', Validators.required],
      expireDate: [''],
      group: [this.documentGroup.NotSet, Validators.required],
      documentCategory: ['', Validators.required],
      businessServiceId: ['', Validators.required],
    });

    if (this.documentData.document) {
      this.setDocumentUpdateData(this.documentData);
    } else {
      this.setDocumentCreateData(this.documentData);
    }
  }

  // getter - access to form fields
  get f() {
    return this.formServiceDocument.controls;
  }

  // Upload selected image
  onSubmit() {
    this.isLoading = true;
    this.submitted = true;
    // If invalid
    if (this.formServiceDocument.invalid) {
      this.isLoading = false;
      return;
    }

    if (this.documentData.document?.link === this.formServiceDocument.value.link) {
      this.saveForm(); // Updating & not updating the file
    } else {
      this.uploadDocToS3(); // updating the file
    }
  }

  uploadDocToS3() {
    this.formData = new FormData();
    this.formData.append('file', this.fileData);

    const fileName = this.getFileNameByCategory(
      this.formServiceDocument.value.name
    );
    const contentType = this.fileData.type;
    const fileExtension = this.fileData.name.split('.').pop();

    const bucket = new AWS.S3({
      accessKeyId: environment.config.keyId,
      secretAccessKey: environment.config.key,
      region: environment.config.region,
    });

    const folderName = `Business_Services/${this.documentData.businessServiceId}`
    const fileLocation = `Trade/${this.documentData.businessInfoId}/Documents/${folderName}/`+ fileName +'.'+ fileExtension;

    const params = {
      Bucket: environment.config.location,
      Key: fileLocation,
      Body: this.fileData,
      ACL: 'private',
      ContentType: contentType,
    };

    bucket.upload(params, (error: any, response: any) => {
      if (error) {
        this.alertService.error('Operation failed!');
        return false;
      }

      this.formServiceDocument.patchValue({
        link: response.Key,
      });
      this.saveForm();
      return true;
    });
  }

  saveForm() {
    if (this.documentData?.document?.id) {
      this.updateBusinessServiceDocument(this.formServiceDocument.value);
    } else {
      this.createBusinessServiceDocument(this.formServiceDocument.value);
    }
  }

  // Browse file/document and preview.
  fileProgress(event: any) {
    this.reader = new FileReader();
    this.fileName = '';
    this.fileData = event.target.files[0];
    this.fileName = event.target.files[0].name;
    this.formServiceDocument.patchValue({
      link: this.fileName,
    });
    const fileExtension = this.fileData.name.split('.').pop();
    const docType = this.getFileExtention(fileExtension); // set file extention
    this.formServiceDocument.controls['documentType'].setValue(docType);
    // For iamge preview
    this.imagePath = event.target.files;
    this.reader.readAsDataURL(event.target.files[0]);
    this.reader.onload = (_event) => {
      this.imgURL = this.reader.result;
    };
  }

  // Create business service document
  private createBusinessServiceDocument(data: BusinessServiceDocumentModel) {
    this.isLoading = true;
    this.serviceDocumentData = data;
    this.serviceDocumentData.expireDate = this.convertDate(data.expireDate)
    this.tradieService
      .addBusinessServiceDocument(this.serviceDocumentData).subscribe({
        next: (response: any) => {
          if (response.success) {
            this.alertService.success('Document created successfully');
            this.dialogRef.close(response.data);
          } else {
            this.alertService.error(response.error.message);
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          this.alertService.error('Operation failed!');
        },
      });
  }

  // Edit business service document
  private updateBusinessServiceDocument(data: BusinessDocumentUpdateModel) {
    this.isLoading = true;
    this.serviceDocumentData = data;
    this.serviceDocumentData.expireDate = this.convertDate(data.expireDate)
    this.tradieService
      .updateBusinessServiceDocument(this.serviceDocumentData)
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.alertService.success('Document updated successfully');
            this.dialogRef.close(response.data);
          } else {
            this.alertService.error(response.error.message);
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          this.isLoading = false;
          this.alertService.error('Operation failed!');
        },
      });
  }

  // Is Update
  setDocumentUpdateData(serviceDocument: CustomDocumentModel) {
    let docType = this.getFileExtention(serviceDocument.document.link.split('.').pop())
    this.formServiceDocument.addControl('id', new FormControl(serviceDocument.document.id) );
    this.formServiceDocument.controls['name'].setValue(serviceDocument.document.name);
    this.formServiceDocument.controls['link'].setValue(serviceDocument.document.link);
    this.formServiceDocument.controls['documentType'].setValue(docType);
    const expireDate = serviceDocument.document.expireDate ? serviceDocument.document.expireDate : null
    this.formServiceDocument.controls['expireDate'].setValue(expireDate);
    this.formServiceDocument.controls['group'].setValue(this.documentGroup.NotSet);
    this.formServiceDocument.controls['documentCategory'].setValue(serviceDocument.document.documentCategory);
    this.formServiceDocument.controls['businessServiceId'].setValue(serviceDocument.businessServiceId);
  }

  setDocumentCreateData(serviceDocument: CustomDocumentModel) {
    this.formServiceDocument.controls['group'].setValue(this.documentGroup.NotSet);
    this.formServiceDocument.controls['documentCategory'].setValue(serviceDocument.documentCategory);
    this.formServiceDocument.controls['businessServiceId'].setValue(serviceDocument.businessServiceId);
  }

  // Generate File Name
  getFileNameByCategory(name: string) {
    const timePrefix = this.datePipe.transform(new Date(), 'yyyyMMddhhmmss');
    return name ? name.split(' ').join('_').toLocaleLowerCase() + '_' + timePrefix : '';
  }

  // --- SET FILE / DOCUMENT TYPE --- //
  getFileExtention(fileExtension: any) {
    let extention;
    switch ( fileExtension.toLowerCase() ) {
      case 'pdf': 
        extention = this.documentType.PDF 
        break;
      case 'doc': 
        extention = this.documentType.DOC 
        break;
      case 'docx': 
        extention = this.documentType.DOC 
        break;
      case 'png': 
        extention = this.documentType.PNG 
        break;
      case 'jpeg': 
        extention = this.documentType.JPEG 
        break;
      case 'jpg': 
        extention = this.documentType.JPEG 
        break;
    }
    return extention
  }

  convertDate(date: string) {
    return this.datePipe.transform( date,'yyyy-MM-dd' ); 
  }

  clearDate() {
    this.formServiceDocument.controls['expireDate'].setValue(null);
  }
}
